import {
  Button,
  Flex,
  Heading,
  VStack,
  Grid,
  GridItem,
  Box,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useHistory, useParams } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import { FocusError } from "../../components/FocusError";
import * as Yup from "yup";
import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";

import CriancaService from "../../../main/services/CriancaService";
import {
  format,
  intervalToDuration,
  isAfter,
  isEqual,
  isValid,
  parse,
} from "date-fns";
import { CustomSelectWField } from "../../components/CustomSelectWField";
import { CustomDatepickerWField } from "../../components/CustomDatepickerWField";
import VisitasService from "../../../main/services/VisitasService";
import ServicoAcolhimentoService from "../../../main/services/ServicoAcolhimentoService";
import { criancaFunc, visitasFunc } from "../../../helpers/Funcionalidades";
import { useAuthProvider } from "../../../Context/AuthProvider";
import SemRegistros from "../../components/SemRegistros";

const schema = Yup.object().shape({
  idTipoReferencia: Yup.object().when(["gestor", "id", "desligado"], {
    is: (gestor, id, desligado) => {
      if (id) {
        if (desligado) {
          return false;
        }
        return true;
      } else {
        return true;
      }
    },
    then: Yup.object().nullable().required("Este campo é obrigatório!"),
    otherwise: Yup.object().nullable().notRequired(),
  }),

  crianca: Yup.object().nullable().required("Este campo é obrigatório!"),
  visitante: Yup.object().nullable().required("Este campo é obrigatório!"),
  data: Yup.string()
    .required("Este campo é obrigatório!")
    .nullable()
    .test("validDate", "Data inválida!", (value) => {
      if (!!value) {
        if (value.length === 10) {
          return isValid(parse(value, "dd/MM/yyyy", new Date()));
        } else {
          return false;
        }
      } else {
        return true;
      }
    })
    .test(
      "dataNascimentoAfterToday",
      "A data informada não pode ser uma data futura!",
      (value) => {
        if (!value) {
          return true;
        } else {
          return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
        }
      }
    )
    .test(
      "dataNascimentoGreaterThan100",
      "A idade informada não pode ser superior a 100 anos!",
      (value) => {
        if (!value) {
          return true;
        } else {
          try {
            const parsedDate = parse(value, "dd/MM/yyyy", new Date(), {
              awareOfUnicodeTokens: true,
            });

            if (!isValid(parsedDate)) {
              return false;
            }
            const idade = intervalToDuration({
              start: parsedDate,
              end: new Date(),
            }).years;

            return idade <= 100;
          } catch (error) {
            return false;
          }
        }
      }
    )
    .test(
      "dataVisitaAfterNascimento",
      "Data da visita informada menor que a data de nascimento do acolhido.",
      function (value) {
        const { data, crianca } = this.parent;

        // Verifica se a criança está selecionada e se a data de nascimento existe
        if (crianca && crianca.dataNascimento && data) {
          const dataNascimento = new Date(crianca.dataNascimento);

          const partesData = value.split("/");
          const dataVisita = new Date(
            partesData[2],
            partesData[1] - 1,
            partesData[0]
          );

          return (
            isAfter(dataVisita, dataNascimento) ||
            isEqual(dataVisita, dataNascimento)
          );
        }

        return true;
      }
    ),
});

const CadastrarVisitas = () => {
  document.title = "MCA - Visitas - Cadastrar";
  let history = useHistory();

  const { id } = useParams();
  const { roleKeycloack, user } = useAuthProvider();
  const gestor = roleKeycloack.includes(criancaFunc.visualizarTudo);
  const [loading, setLoading] = useState(true);
  const [selectedCrianca, setSelectedCrianca] = useState(null);

  const [relations, setRelations] = useState({ options: [], isLoading: false });
  const today = new Date();
  const formattedToday = format(today, "dd/MM/yyyy", { locale: pt });
  const [selectedServico, setSelectedServico] = useState(false);
  const [opcoesServico, setOpcoesServico] = useState({
    options: [],
    isLoading: false,
  });
  const [opcoesCrianca, setOpcoesCrianca] = useState({
    options: [],
    isLoading: false,
  });

  const [initialValues, setInitialValues] = useState({
    idTipoReferencia: "",
    crianca: "",
    visitante: "",
    data: "",
    id: id,
    gestor: gestor,
    desligado: selectedCrianca?.desligado,
    unidadesEscolares: [{ nome: "", escolaAtual: true }],
  });

  const validation = () => {
    if (id) {
      if (id && gestor && !initialValues.crianca.desligado) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  };

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  useEffect(() => {
    const notFoud = () => {
      Swal.fire({
        title: "Atenção!",
        text: "Registro não encontrado",
        icon: "warning",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#169499",
      }).then(() => {
        history.push("/visitas/lista");
      });
    };
    const fetchData = () => {
      ServicoAcolhimentoService.getServicosAcolhimentoAtivosSuspensosLogado()
        .then((res) => {
          const filteredData = res.data.filter((item) =>
            user?.orgaoExterno?.includes(item.pessoaJuridica.id)
          );

          const formattedOptions = filteredData[0];

          if (filteredData) {
            setSelectedServico(true);
          }
          setOpcoesServico({ options: filteredData, isLoading: false });

          return formattedOptions;
        })
        .then((formattedOptions) => {
          if (!id) {
            return { formattedOptions };
          }
          return VisitasService.getVisita(id).then((visitaRes) => ({
            formattedOptions,
            visitaData: visitaRes.data,
          }));
        })
        .then(({ formattedOptions, visitaData }) => {
          setInitialValues({
            ...initialValues,
            idTipoReferencia: formattedOptions || "",
            data: formattedToday,
            id: id,
            gestor: gestor,
            desligado: selectedCrianca?.desligado,
            crianca: visitaData
              ? {
                  id: visitaData.idCrianca,
                  nome: visitaData.pessoaFisicaCriancaResponse?.nome || "",
                }
              : null,
          });

          setLoading(false);
        })
        .catch((err) => {
          notFoud();
        });
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formattedToday,
    gestor,
    history,
    id,
    selectedCrianca?.desligado,
    user?.orgaoExterno,
  ]);

  const addVisitaCrianca = async (id, valuesToSend) => {
    try {
      const res = await VisitasService.postVisitas(id, valuesToSend);
      Swal.fire({
        title: "Sucesso!",
        html: `Registro cadastrado com sucesso!`,
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#169499",
      }).then((result) => {
        if (result.isConfirmed) {
          history.push(`/visitas/${res.data.idVisita}`);
        }
      });
    } catch (error) {
      console.log(error);
      Swal.fire({
        title: "Atenção!",
        text: error.response.data.message ? error.response.data.message : "",
        icon: "warning",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#169499",
      });
    }
  };

  const handleGetRelacionamento = (props) => {
    let dataFormatada;
    setRelations({ ...[], isLoading: true });

    if (
      props.values.data &&
      isValid(parse(props.values.data, "dd/MM/yyyy", new Date()))
    ) {
      dataFormatada = format(
        parse(props.values.data, "dd/MM/yyyy", new Date()),
        "yyyy-MM-dd"
      );
    }

    VisitasService.relacionamentosVisita(
      props.values.crianca.id,
      dataFormatada ? dataFormatada : "",
      props?.values?.idTipoReferencia?.id
        ? props?.values?.idTipoReferencia?.id
        : null
    )
      .then((res) => {
        setRelations({ options: res.data, isLoading: false });
      })
      .catch((error) => {
        setRelations({ options: [], isLoading: false });
      });
  };

  const handleGetCrianca = () => {
    setOpcoesCrianca({ ...opcoesCrianca, isLoading: true });
    const carregar =
      roleKeycloack && roleKeycloack.includes(visitasFunc.visitaCarregarCrianca)
        ? true
        : false;
    CriancaService.listarCriancaServicoAcolhimento(
      initialValues?.idTipoReferencia.id,
      carregar
    ).then((res) => {
      setSelectedCrianca(res.data);
      setOpcoesCrianca({ options: res.data, isLoading: false });
    });
  };

  const limparCadastro = (props) => {
    props.setFieldValue("crianca", "");
    props.setFieldValue("visitante", "");

    setRelations({ options: [], isLoading: false });
    setInitialValues({ ...initialValues, crianca: "", visitante: "" });
  };

  return !loading ? (
    <Flex
      pt={"20px"}
      px={["5px", "30px"]}
      h="100%"
      flexDir="column"
      w="100%"
      maxW="2000px"
      overflow="auto"
      pb={["10px", "20px"]}
    >
      <Flex flexDir="row" justifyContent="space-between">
        <Heading fontSize="large">Cadastrar Visita</Heading>

        <Button
          colorScheme={"teal"}
          px={16}
          onClick={() => {
            history.push("/visitas/lista");
          }}
        >
          Voltar
        </Button>
      </Flex>
      {roleKeycloack.includes(visitasFunc.visitaCadastrar) ? (
        <Formik
          validationSchema={schema}
          initialValues={initialValues}
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            const { crianca, visitante, data } = values;

            const splitDate = data.split("/");
            const year = parseInt(splitDate[2]);
            const month = parseInt(splitDate[1]) - 1;
            const day = parseInt(splitDate[0]);
            const now = new Date();

            const formattedDate = new Date(
              year,
              month,
              day,
              now.getHours(),
              now.getMinutes(),
              now.getSeconds()
            );
            const formattedDateString = format(
              formattedDate,
              "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
            );

            const criancaAdolescente = {
              idCrianca: crianca ? crianca.id : "",
            };

            const valuesToSend = {
              dataVisita: data ? formattedDateString : "",
              idPessoaFisicaVisitante: visitante ? visitante.id : "",
            };

            addVisitaCrianca(criancaAdolescente.idCrianca, valuesToSend);
            actions.setSubmitting(false);
          }}
        >
          {(props) => (
            <Flex flexDir="column" mt={4}>
              <Form>
                <FocusError />
                <Box bgColor="#FFF" borderRadius="16px" p="20px">
                  <Box w={{ md: "60%" }}>
                    <Flex>
                      <CustomSelectWField
                        fieldName="idTipoReferencia"
                        label="Serviço de Acolhimento*"
                        placeholder="Selecione o Serviço de Acolhimento"
                        getOptionLabel={(option) =>
                          option?.pessoaJuridica?.nomeFantasia ||
                          option?.nomeFantasia
                        }
                        getOptionValue={(option) => option.id}
                        isDisabled={true}
                        options={opcoesServico.options}
                        isLoading={opcoesServico.isLoading}
                      />
                    </Flex>

                    <Flex mt={5}>
                      <CustomSelectWField
                        fieldName="crianca"
                        label="Criança / Adolescente*"
                        message={
                          !initialValues.idTipoReferencia
                            ? "Sem opções selecione uma Servico de Acolhimento"
                            : "Sem Opções"
                        }
                        placeholder="Selecione a Criança/Adolescente"
                        getOptionLabel={(option) => option.nome}
                        getOptionValue={(option) => option.id}
                        isLoading={!id && opcoesCrianca.isLoading}
                        options={
                          !id || gestor
                            ? opcoesCrianca.options
                            : selectedCrianca
                        }
                        defaultValue={id && selectedCrianca}
                        isDisabled={validation()}
                        onChange={(value) => {
                          props.setFieldValue("crianca", value);
                          if (!id || gestor) {
                            props.setFieldValue("visitante", "");
                            setRelations({ options: [], isLoading: false });
                          }
                        }}
                        onFocus={
                          initialValues.idTipoReferencia &&
                          (!id || gestor) &&
                          opcoesCrianca.options.length <= 0
                            ? handleGetCrianca
                            : null
                        }
                      />
                    </Flex>
                    <Grid mt={5} templateColumns="repeat(5, 1fr) 1fr" gap={2}>
                      <GridItem colSpan={{ base: 4, lg: 3, xl: 2 }}>
                        <VStack alignItems="flex-start">
                          <CustomDatepickerWField
                            fieldName="data"
                            label="Data*"
                            placeholder="DD/MM/AAAA"
                            maxDate={new Date()}
                            clear={() => props.setFieldValue("visitante", "")}
                          />
                        </VStack>
                      </GridItem>

                      <GridItem colSpan={{ base: 6, xl: 4 }}>
                        <CustomSelectWField
                          fieldName="visitante"
                          label="Visitante*"
                          placeholder="Selecione o Visitante"
                          message={
                            !initialValues.crianca
                              ? "Sem opções selecione uma Criança / Adolescente"
                              : "Sem Opções"
                          }
                          getOptionLabel={(option) => option.nome}
                          getOptionValue={(option) => option.id}
                          isLoading={relations.isLoading}
                          options={relations.options}
                          onChange={(value) => {
                            props.setFieldValue(
                              "visitante",
                              value ? value : ""
                            );
                          }}
                          onFocus={() => handleGetRelacionamento(props)}
                        />
                      </GridItem>
                    </Grid>
                  </Box>
                </Box>
                <Flex mt={10} gap={6} justifyContent="end">
                  {selectedServico && (
                    <Button
                      mt={4}
                      onClick={() => limparCadastro(props)}
                      colorScheme="teal"
                      px={16}
                    >
                      Limpar
                    </Button>
                  )}

                  <Button
                    mt={4}
                    colorScheme="teal"
                    px={16}
                    isLoading={props.isSubmitting}
                    type="submit"
                    translate="no"
                  >
                    Salvar
                  </Button>
                </Flex>
              </Form>
            </Flex>
          )}
        </Formik>
      ) : (
        <Box mt={4}>
          <SemRegistros buttonVisible={true} half />
        </Box>
      )}
    </Flex>
  ) : (
    <Flex w="100%" mt="25%" justifyContent="center">
      <LoadingSpinner />
    </Flex>
  );
};
export default CadastrarVisitas;
